<script setup>
// NOOVI: KPIs de desempenho com tendência vs. período anterior na Overview.
// O endpoint (reports#summary → V2::Reports::Conversations::MetricBuilder)
// já devolve o período anterior junto (`previous`) e já tem uma action/getter
// Vuex prontos (fetchAccountSummary/getAccountSummary) — usados hoje só pela
// página /reports/conversation via ChartStats.vue. Aqui é só um novo
// consumidor da mesma fonte, reaproveitando useReportMetrics() para o cálculo
// de tendência e a formatação de duração (mesma lógica de ChartStats.vue).
// Período vem do filtro global (LiveReports.vue via provide/inject) — não
// mais um 7 dias fixo local.
import { computed, inject, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import { STATUS } from 'dashboard/store/constants';
import MetricCard from 'dashboard/components-next/Reports/MetricCard.vue';
import OverviewCard from './overview/MetricCard.vue';
import {
  OVERVIEW_DATE_FILTER_KEY,
  createDefaultDateFilter,
} from '../helpers/overviewDateFilter';

const { t } = useI18n();
const store = useStore();
const dateFilter = inject(
  OVERVIEW_DATE_FILTER_KEY,
  createDefaultDateFilter,
  true
);
const { calculateTrend, isAverageMetricType, displayMetric, fetchingStatus } =
  useReportMetrics();

const METRICS = [
  {
    key: 'conversations_count',
    nameKey: 'REPORT.METRICS.CONVERSATIONS.NAME',
    icon: 'i-lucide-message-circle',
    color: 'blue',
  },
  {
    key: 'avg_first_response_time',
    nameKey: 'REPORT.METRICS.FIRST_RESPONSE_TIME.NAME',
    icon: 'i-lucide-timer',
    color: 'teal',
  },
  {
    key: 'avg_resolution_time',
    nameKey: 'REPORT.METRICS.RESOLUTION_TIME.NAME',
    icon: 'i-lucide-circle-check-big',
    color: 'amber',
  },
  {
    key: 'resolutions_count',
    nameKey: 'REPORT.METRICS.RESOLUTION_COUNT.NAME',
    icon: 'i-lucide-list-checks',
    color: 'slate',
  },
];

// Mesma regra de "bom/ruim" de ChartStats.vue (a tela /reports/conversation
// já usa isso): pra métricas de tempo médio, subir é ruim; pra contagens,
// subir é bom. Reaproveitada aqui, não reinventada.
const isTrendPositive = (key, trend) =>
  isAverageMetricType(key) ? trend <= 0 : trend >= 0;

const cards = computed(() =>
  METRICS.map(metric => {
    const trend = calculateTrend(metric.key);
    return {
      ...metric,
      title: t(metric.nameKey),
      value: displayMetric(metric.key),
      trendLabel: `${Math.abs(trend)}%`,
      // A seta segue o SINAL do número (subiu/desceu); a cor segue se isso é
      // bom ou ruim pra essa métrica — não são a mesma coisa (ver MetricCard.vue).
      trendDirection: trend >= 0 ? 'up' : 'down',
      trendPositive: isTrendPositive(metric.key, trend),
    };
  })
);

const isLoading = computed(() => fetchingStatus.value === STATUS.FETCHING);

const fetchSummary = () => {
  store.dispatch('fetchAccountSummary', {
    from: dateFilter.from,
    to: dateFilter.to,
    businessHours: dateFilter.businessHours,
    type: 'account',
  });
};

onMounted(fetchSummary);
watch(
  () => [dateFilter.from, dateFilter.to, dateFilter.businessHours],
  fetchSummary
);
</script>

<template>
  <OverviewCard
    :is-loading="isLoading"
    :loading-message="t('OVERVIEW_REPORTS.PERFORMANCE_SUMMARY.LOADING_MESSAGE')"
  >
    <template #header>
      <div class="flex items-center gap-2 flex-row flex-wrap min-w-0">
        <h5 class="mb-0 text-n-slate-12 font-medium text-lg break-words">
          {{ t('OVERVIEW_REPORTS.PERFORMANCE_SUMMARY.HEADER') }}
        </h5>
      </div>
    </template>
    <div class="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <MetricCard
        v-for="card in cards"
        :key="card.key"
        :title="card.title"
        :value="card.value"
        :icon="card.icon"
        :color="card.color"
        :trend-label="card.trendLabel"
        :trend-direction="card.trendDirection"
        :trend-positive="card.trendPositive"
      />
    </div>
  </OverviewCard>
</template>
